/**
 * Medi-Cal Credential Setup Wizard
 * Admin UX for onboarding provider credentials:
 * Step 1: Provider Identity (NPI, Medi-Cal Provider #)
 * Step 2: Clearinghouse Config (Availity client ID/secret, payer ID)
 * Step 3: CAMMIS Direct (submitter ID, SFTP credentials) — optional
 * Step 4: Delegation Authorization (BAA, POS agreement)
 * Step 5: Test & Validate
 */

import React, { useState, useEffect } from 'react';
import { eligibilityApi, CredentialStatus } from '@/services/api/eligibility';
import { useTabbedChrome } from '@/components/weavein';

interface StepDef {
  id: number;
  name: string;
  description: string;
}

const STEPS: StepDef[] = [
  { id: 1, name: 'Provider Identity', description: 'NPI and Medi-Cal Provider Number' },
  { id: 2, name: 'Clearinghouse', description: 'Availity credentials for 270/271 transactions' },
  { id: 3, name: 'CAMMIS Direct', description: 'Optional: Direct DHCS connection for $0/query' },
  { id: 4, name: 'Delegation', description: 'BAA and POS agreement authorization' },
  { id: 5, name: 'Validate', description: 'Test credentials and go live' },
];

const CredentialSetupWizard: React.FC = () => {
  const tabbed = useTabbedChrome();
  const [currentStep, setCurrentStep] = useState(1);
  const [status, setStatus] = useState<CredentialStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<any>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    provider_npi: '',
    medi_cal_provider_number: '',
    clearinghouse_name: 'availity',
    clearinghouse_provider_id: '',
    availity_client_id: '',
    availity_client_secret: '',
    cammis_submitter_id: '',
    cammis_user_id: '',
    cammis_pin: '',
    delegation_authorized_by: '',
    baa_on_file: false,
    pos_agreement_signed: false,
  });

  useEffect(() => {
    loadCredentialStatus();
  }, []);

  const loadCredentialStatus = async () => {
    setLoading(true);
    try {
      const response = await eligibilityApi.getCredentialStatus();
      setStatus(response.data);
      if (response.data.provider_npi) {
        setFormData(prev => ({ ...prev, provider_npi: response.data.provider_npi || '' }));
      }
    } catch {
      // No credentials yet
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCredentials = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await eligibilityApi.storeCredentials({
        provider_npi: formData.provider_npi,
        medi_cal_provider_number: formData.medi_cal_provider_number,
        clearinghouse_name: formData.clearinghouse_name,
        clearinghouse_provider_id: formData.clearinghouse_provider_id,
        cammis_submitter_id: formData.cammis_submitter_id,
        cammis_user_id: formData.cammis_user_id,
        cammis_pin: formData.cammis_pin,
        delegation_authorized_by: formData.delegation_authorized_by,
      });
      setSuccess('Credentials saved successfully');
      await loadCredentialStatus();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save credentials');
    } finally {
      setSaving(false);
    }
  };

  const handleAuthorizeDelegation = async () => {
    setSaving(true);
    setError('');
    try {
      await eligibilityApi.authorizeDelegation(
        formData.delegation_authorized_by,
        formData.baa_on_file,
        formData.pos_agreement_signed,
      );
      setSuccess('Delegation authorized');
      await loadCredentialStatus();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to authorize delegation');
    } finally {
      setSaving(false);
    }
  };

  const handleTestCredentials = async () => {
    setTesting(true);
    setTestResult(null);
    setError('');
    try {
      const response = await eligibilityApi.validateCredentials();
      setTestResult(response.data);
      setSuccess('Credentials validated successfully');
      await loadCredentialStatus();
    } catch (err: any) {
      setTestResult({ status: 'FAILED', error: err.response?.data?.detail || 'Validation failed' });
    } finally {
      setTesting(false);
    }
  };

  const updateField = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return <div className="p-8 text-center text-gray-400">Loading credential status...</div>;
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        {!tabbed && (
        <h2 className="text-lg font-semibold">Medi-Cal Eligibility Credentials</h2>
        )}
        <p className="text-sm text-gray-500 mt-1">
          Configure provider credentials for real-time eligibility verification via DHCS/CAMMIS and clearinghouse.
        </p>
      </div>

      {/* Status Banner */}
      {status && (
        <div className={`p-4 rounded-lg border ${
          status.status === 'ACTIVE' ? 'bg-green-50 border-green-200' :
          status.status === 'PENDING_VERIFICATION' ? 'bg-yellow-50 border-yellow-200' :
          'bg-gray-50 border-gray-200'
        }`}>
          <div className="flex items-center justify-between">
            <div>
              <span className="font-medium text-sm">Status: {status.status?.replace(/_/g, ' ')}</span>
              {status.provider_npi && <span className="ml-3 text-xs text-gray-500">NPI: {status.provider_npi}</span>}
              {status.last_verified && <span className="ml-3 text-xs text-gray-500">Last verified: {status.last_verified}</span>}
            </div>
            <div className="flex gap-1">
              {status.channels_available.map(ch => (
                <span key={ch} className="text-xs bg-white px-2 py-0.5 rounded border">{ch.replace(/_/g, ' ')}</span>
              ))}
            </div>
          </div>
          {status.missing.length > 0 && (
            <div className="mt-2 text-xs text-gray-500">
              Missing: {status.missing.map(m => m.replace(/_/g, ' ')).join(', ')}
            </div>
          )}
        </div>
      )}

      {/* Step Navigation */}
      <div className="flex gap-1">
        {STEPS.map(step => (
          <button
            key={step.id}
            onClick={() => setCurrentStep(step.id)}
            className={`flex-1 p-3 rounded-lg text-left text-sm transition-colors ${
              currentStep === step.id
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
            }`}
          >
            <div className="font-medium">Step {step.id}: {step.name}</div>
            <div className={`text-xs mt-0.5 ${currentStep === step.id ? 'text-blue-100' : 'text-gray-500'}`}>
              {step.description}
            </div>
          </button>
        ))}
      </div>

      {/* Messages */}
      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">{error}</div>}
      {success && <div className="p-3 bg-green-50 border border-green-200 rounded-md text-green-700 text-sm">{success}</div>}

      {/* Step Content */}
      <div className="bg-white rounded-lg shadow p-6">
        {currentStep === 1 && (
          <div className="space-y-4">
            <h3 className="font-semibold">Provider Identity</h3>
            <p className="text-sm text-gray-500">
              Your organization's NPI and Medi-Cal provider number. Required for all verification channels.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">National Provider Identifier (NPI)</label>
                <input
                  type="text" maxLength={10} placeholder="10-digit NPI"
                  value={formData.provider_npi}
                  onChange={e => updateField('provider_npi', e.target.value.replace(/\D/g, '').slice(0, 10))}
                  className="w-full border rounded-md px-3 py-2 text-sm"
                />
                <p className="text-xs text-gray-400 mt-1">Organization NPI from NPPES</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Medi-Cal Provider Number</label>
                <input
                  type="text" placeholder="Medi-Cal provider #"
                  value={formData.medi_cal_provider_number}
                  onChange={e => updateField('medi_cal_provider_number', e.target.value)}
                  className="w-full border rounded-md px-3 py-2 text-sm"
                />
                <p className="text-xs text-gray-400 mt-1">From PAVE enrollment</p>
              </div>
            </div>
            <div className="flex justify-end">
              <button onClick={() => { handleSaveCredentials(); setCurrentStep(2); }} disabled={saving || !formData.provider_npi}
                className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm hover:bg-blue-700 disabled:opacity-50">
                {saving ? 'Saving...' : 'Save & Continue'}
              </button>
            </div>
          </div>
        )}

        {currentStep === 2 && (
          <div className="space-y-4">
            <h3 className="font-semibold">Clearinghouse Configuration</h3>
            <p className="text-sm text-gray-500">
              Select a clearinghouse for 270/271 eligibility verification. Stedi is recommended for faster onboarding and per-CBO credential isolation.
            </p>

            {/* Clearinghouse Selector */}
            <div className="grid grid-cols-2 gap-3">
              <button type="button" onClick={() => updateField('clearinghouse_name', 'stedi')}
                className={`p-4 rounded-lg border-2 text-left transition-all ${
                  formData.clearinghouse_name === 'stedi' ? 'border-green-400 bg-green-50 ring-2 ring-green-300' : 'border-gray-200 hover:border-gray-300'
                }`}>
                <div className="font-semibold text-sm">Stedi (Recommended)</div>
                <div className="text-xs text-gray-500 mt-1">$0.15/check, API-first, no enrollment needed for CA Medi-Cal 270</div>
                <div className="text-xs text-green-600 mt-1 font-medium">Same-day onboarding</div>
              </button>
              <button type="button" onClick={() => updateField('clearinghouse_name', 'availity')}
                className={`p-4 rounded-lg border-2 text-left transition-all ${
                  formData.clearinghouse_name === 'availity' ? 'border-blue-400 bg-blue-50 ring-2 ring-blue-300' : 'border-gray-200 hover:border-gray-300'
                }`}>
                <div className="font-semibold text-sm">Availity</div>
                <div className="text-xs text-gray-500 mt-1">Enterprise, OAuth2, requires per-NPI payer enrollment</div>
                <div className="text-xs text-yellow-600 mt-1 font-medium">2-6 week enrollment per payer</div>
              </button>
            </div>

            {/* Stedi Config */}
            {formData.clearinghouse_name === 'stedi' && (
              <div className="space-y-3">
                <div className="bg-green-50 p-3 rounded-md text-sm text-green-700">
                  <strong>Stedi Setup (3 steps):</strong>
                  <ol className="list-decimal ml-4 mt-1 space-y-1 text-xs">
                    <li>Create account at <span className="font-mono">stedi.com</span> (free, no credit card)</li>
                    <li>Generate API key in Dashboard &gt; Settings &gt; API Keys</li>
                    <li>Paste below — CA Medi-Cal eligibility works immediately (payer: IAUKR, no enrollment)</li>
                  </ol>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Stedi API Key</label>
                  <input
                    type="password" placeholder="Paste your Stedi API key"
                    value={formData.clearinghouse_provider_id}
                    onChange={e => updateField('clearinghouse_provider_id', e.target.value)}
                    className="w-full border rounded-md px-3 py-2 text-sm"
                  />
                  <p className="text-xs text-gray-400 mt-1">From Stedi Dashboard &gt; Settings &gt; API Keys. For per-CBO keys, use Stedi Integrated Accounts.</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-2 bg-gray-50 rounded text-xs">
                    <span className="text-gray-500">CA Medi-Cal Payer ID:</span> <span className="font-mono font-medium">IAUKR</span>
                  </div>
                  <div className="p-2 bg-gray-50 rounded text-xs">
                    <span className="text-gray-500">Enrollment required:</span> <span className="font-medium text-green-600">No (for 270)</span>
                  </div>
                </div>
              </div>
            )}

            {/* Availity Config */}
            {formData.clearinghouse_name === 'availity' && (
              <div className="space-y-3">
                <div className="bg-blue-50 p-3 rounded-md text-sm text-blue-700">
                  <strong>Availity Setup Steps:</strong>
                  <ol className="list-decimal ml-4 mt-1 space-y-1 text-xs">
                    <li>Register at <span className="font-mono">availity.com</span> as a provider organization</li>
                    <li>Request API access (Eligibility scope) from Availity support</li>
                    <li>Receive OAuth2 client_id and client_secret</li>
                    <li>Enroll NPI with CA Medicaid payer (ID: 57016) in Availity portal</li>
                    <li>Test in sandbox environment before switching to production</li>
                  </ol>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Availity Client ID</label>
                    <input type="text" placeholder="OAuth2 client ID"
                      value={formData.availity_client_id}
                      onChange={e => updateField('availity_client_id', e.target.value)}
                      className="w-full border rounded-md px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Availity Client Secret</label>
                    <input type="password" placeholder="OAuth2 client secret"
                      value={formData.availity_client_secret}
                      onChange={e => updateField('availity_client_secret', e.target.value)}
                      className="w-full border rounded-md px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Clearinghouse Provider ID</label>
                    <input type="text" placeholder="Provider ID in Availity"
                      value={formData.clearinghouse_provider_id}
                      onChange={e => updateField('clearinghouse_provider_id', e.target.value)}
                      className="w-full border rounded-md px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">CA Medicaid Payer ID</label>
                    <input type="text" value="57016" readOnly
                      className="w-full border rounded-md px-3 py-2 text-sm bg-gray-50 text-gray-500" />
                  </div>
                </div>
              </div>
            )}
            <div className="flex justify-between">
              <button onClick={() => setCurrentStep(1)} className="text-sm text-gray-500 hover:text-gray-700">Back</button>
              <button onClick={() => { handleSaveCredentials(); setCurrentStep(3); }} disabled={saving}
                className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm hover:bg-blue-700 disabled:opacity-50">
                {saving ? 'Saving...' : 'Save & Continue'}
              </button>
            </div>
          </div>
        )}

        {currentStep === 3 && (
          <div className="space-y-4">
            <h3 className="font-semibold">CAMMIS Direct Connection (Optional)</h3>
            <p className="text-sm text-gray-500">
              Direct connection to DHCS CAMMIS for $0/query eligibility verification. Requires DHCS Form 6153 enrollment.
            </p>
            <div className="bg-amber-50 p-3 rounded-md text-sm text-amber-700">
              <strong>DHCS CAMMIS Enrollment Steps:</strong>
              <ol className="list-decimal ml-4 mt-1 space-y-1 text-xs">
                <li>Submit <strong>DHCS Form 6153</strong> to obtain CMC Submitter ID (2-4 months processing)</li>
                <li>Sign <strong>POS Network/Internet Agreement</strong> with DHCS</li>
                <li>Register as Submitter Organization in CAMMIS Portal</li>
                <li>Complete mandatory test transactions in CAMMIS test environment</li>
                <li>Receive Secure Token by mail and activate</li>
              </ol>
              <p className="mt-2 text-xs"><strong>Contact:</strong> Medi-Cal TSC: (800) 541-5555 | CMC Help Desk: (916) 636-1100</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">CMC Submitter ID</label>
                <input
                  type="text" maxLength={10} placeholder="3-character Submitter ID"
                  value={formData.cammis_submitter_id}
                  onChange={e => updateField('cammis_submitter_id', e.target.value)}
                  className="w-full border rounded-md px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">CAMMIS User ID</label>
                <input
                  type="text" placeholder="Portal user ID"
                  value={formData.cammis_user_id}
                  onChange={e => updateField('cammis_user_id', e.target.value)}
                  className="w-full border rounded-md px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">CAMMIS PIN</label>
                <input
                  type="password" placeholder="Portal PIN"
                  value={formData.cammis_pin}
                  onChange={e => updateField('cammis_pin', e.target.value)}
                  className="w-full border rounded-md px-3 py-2 text-sm"
                />
              </div>
            </div>
            <div className="flex justify-between">
              <button onClick={() => setCurrentStep(2)} className="text-sm text-gray-500 hover:text-gray-700">Back</button>
              <div className="flex gap-2">
                <button onClick={() => setCurrentStep(4)} className="text-sm text-gray-500 hover:text-gray-700 border px-3 py-2 rounded-md">
                  Skip (use clearinghouse only)
                </button>
                <button onClick={() => { handleSaveCredentials(); setCurrentStep(4); }} disabled={saving}
                  className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm hover:bg-blue-700 disabled:opacity-50">
                  {saving ? 'Saving...' : 'Save & Continue'}
                </button>
              </div>
            </div>
          </div>
        )}

        {currentStep === 4 && (
          <div className="space-y-4">
            <h3 className="font-semibold">Delegation Authorization</h3>
            <p className="text-sm text-gray-500">
              Legal authorization for the platform to submit eligibility queries on behalf of your organization.
            </p>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Authorized By (Full Name)</label>
                <input
                  type="text" placeholder="Name of authorizing officer"
                  value={formData.delegation_authorized_by}
                  onChange={e => updateField('delegation_authorized_by', e.target.value)}
                  className="w-full border rounded-md px-3 py-2 text-sm"
                />
                <p className="text-xs text-gray-400 mt-1">Person with authority to authorize delegation (typically compliance officer or executive)</p>
              </div>
              <label className="flex items-start gap-3 p-3 border rounded-md hover:bg-gray-50 cursor-pointer">
                <input
                  type="checkbox" checked={formData.baa_on_file}
                  onChange={e => updateField('baa_on_file', e.target.checked)}
                  className="mt-0.5 rounded"
                />
                <div>
                  <div className="text-sm font-medium">Business Associate Agreement (BAA)</div>
                  <div className="text-xs text-gray-500">
                    Confirms a signed BAA is on file between your organization and this platform for HIPAA-compliant PHI handling during eligibility verification.
                  </div>
                </div>
              </label>
              <label className="flex items-start gap-3 p-3 border rounded-md hover:bg-gray-50 cursor-pointer">
                <input
                  type="checkbox" checked={formData.pos_agreement_signed}
                  onChange={e => updateField('pos_agreement_signed', e.target.checked)}
                  className="mt-0.5 rounded"
                />
                <div>
                  <div className="text-sm font-medium">POS Network/Internet Agreement (DHCS)</div>
                  <div className="text-xs text-gray-500">
                    Confirms the DHCS POS agreement has been signed, authorizing electronic access to Medi-Cal eligibility data on behalf of this provider.
                  </div>
                </div>
              </label>
            </div>
            <div className="flex justify-between">
              <button onClick={() => setCurrentStep(3)} className="text-sm text-gray-500 hover:text-gray-700">Back</button>
              <button
                onClick={() => { handleAuthorizeDelegation(); setCurrentStep(5); }}
                disabled={saving || !formData.delegation_authorized_by || (!formData.baa_on_file && !formData.pos_agreement_signed)}
                className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm hover:bg-blue-700 disabled:opacity-50">
                {saving ? 'Saving...' : 'Authorize & Continue'}
              </button>
            </div>
          </div>
        )}

        {currentStep === 5 && (
          <div className="space-y-4">
            <h3 className="font-semibold">Test & Validate Credentials</h3>
            <p className="text-sm text-gray-500">
              Run a test transaction to verify your credentials are working correctly before going live.
            </p>

            {/* Channel Status */}
            {status && (
              <div className="grid grid-cols-3 gap-3">
                {['PRE_SCREEN', 'CLEARINGHOUSE', 'DIRECT_CAMMIS'].map(channel => {
                  const available = status.channels_available.includes(channel);
                  return (
                    <div key={channel} className={`p-3 rounded-lg border text-center ${
                      available ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
                    }`}>
                      <div className={`text-sm font-medium ${available ? 'text-green-700' : 'text-gray-400'}`}>
                        {available ? 'Ready' : 'Not configured'}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">{channel.replace(/_/g, ' ')}</div>
                    </div>
                  );
                })}
              </div>
            )}

            <button
              onClick={handleTestCredentials}
              disabled={testing}
              className="w-full bg-green-600 text-white px-4 py-3 rounded-md text-sm hover:bg-green-700 disabled:opacity-50 font-medium"
            >
              {testing ? 'Running test transaction...' : 'Run Test Verification'}
            </button>

            {testResult && (
              <div className={`p-4 rounded-lg border ${
                testResult.status === 'ACTIVE' || testResult.status === 'success'
                  ? 'bg-green-50 border-green-200'
                  : 'bg-red-50 border-red-200'
              }`}>
                <div className="font-medium text-sm">
                  Test Result: {testResult.status}
                </div>
                {testResult.error && (
                  <div className="text-xs text-red-600 mt-1">{testResult.error}</div>
                )}
                {testResult.verified_at && (
                  <div className="text-xs text-green-600 mt-1">Verified at: {testResult.verified_at}</div>
                )}
              </div>
            )}

            <div className="flex justify-between">
              <button onClick={() => setCurrentStep(4)} className="text-sm text-gray-500 hover:text-gray-700">Back</button>
            </div>
          </div>
        )}
      </div>

      {/* Help Section */}
      <div className="bg-gray-50 rounded-lg p-4 text-xs text-gray-500">
        <strong>Need help?</strong>
        <div className="grid grid-cols-2 gap-2 mt-2">
          <div>PAVE Enrollment Support: 1-866-252-1949</div>
          <div>Medi-Cal TSC (EDI): (800) 541-5555</div>
          <div>CMC Help Desk: (916) 636-1100</div>
          <div>Conduent EDI: EDICommercialSupportTeam@conduent.com</div>
        </div>
      </div>
    </div>
  );
};

export default CredentialSetupWizard;
